import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Check, X } from "lucide-react";
import api from "../lib/api";
import { extractError } from "../lib/formatError";

const PROVIDER_FIELDS = {
  nowpayments: [
    { key: "api_key", label: "NOWPayments API key", secret: true },
    { key: "ipn_secret", label: "IPN secret (HMAC-SHA512)", secret: true },
  ],
  smtp: [
    { key: "host", label: "SMTP host" },
    { key: "port", label: "Port" },
    { key: "user", label: "User" },
    { key: "password", label: "Password", secret: true },
    { key: "from_email", label: "From email" },
  ],
};

export default function AdminProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // provider name

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/providers");
      setProviders(data.providers);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-providers">
      <Toaster theme="dark" position="top-center" />
      <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)] mb-2">Providers</div>
      <h1 className="font-display italic text-4xl text-[color:var(--ink)] mb-2">Integrations</h1>
      <p className="text-sm text-[color:var(--muted)] mb-6 max-w-xl">
        Secrets are encrypted at rest (Fernet) and only shown redacted after save. Flipping enabled toggles player-facing endpoints.
      </p>

      {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}
      {loading ? <div className="text-[color:var(--muted)] text-sm">Loading…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {providers.map((p) => (
            <div key={p.provider} className="border p-5" style={{ borderColor: p.enabled ? "var(--gold)" : "var(--line)", background: "var(--card)" }} data-testid={`provider-${p.provider}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm uppercase tracking-widest text-[color:var(--ink)]">{p.provider}</div>
                <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 border ${p.enabled ? "text-[color:var(--gold)] border-[color:var(--gold)]" : "text-[color:var(--muted)] border-[color:var(--line)]"}`}>
                  {p.enabled ? "enabled" : "disabled"}
                </span>
              </div>
              <div className="text-[10px] text-[color:var(--muted)] mb-3">
                Config: {p.has_config ? Object.keys(p.config_preview).length + " keys" : "none"}
              </div>
              {p.has_config && (
                <div className="text-[10px] font-mono text-[color:var(--muted)] space-y-0.5 mb-3">
                  {Object.entries(p.config_preview).map(([k, v]) => (
                    <div key={k} className="truncate"><span className="text-[color:var(--muted)]">{k}:</span> {typeof v === "string" ? v : JSON.stringify(v)}</div>
                  ))}
                </div>
              )}
              <button
                onClick={() => setEditing(p.provider)}
                className="w-full py-2 border border-[color:var(--line)] hover:border-[color:var(--gold)] text-[10px] tracking-widest uppercase text-[color:var(--muted)] hover:text-[color:var(--gold)]"
                data-testid={`edit-${p.provider}`}
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditModal
          provider={editing}
          existing={providers.find((x) => x.provider === editing)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); toast.success(`${editing} updated`); }}
        />
      )}
    </div>
  );
}

function EditModal({ provider, existing, onClose, onSaved }) {
  const fields = PROVIDER_FIELDS[provider] || [];
  const [enabled, setEnabled] = useState(existing?.enabled ?? false);
  const [config, setConfig] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      // Only send fields the user actually filled in — server preserves nothing else
      const cleaned = Object.fromEntries(Object.entries(config).filter(([_, v]) => v !== undefined && v !== ""));
      await api.post(`/admin/providers/${provider}`, { enabled, config: cleaned });
      onSaved();
    } catch (e2) { setErr(extractError(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 overflow-auto" style={{ background: "rgba(6,4,3,0.9)", backdropFilter: "blur(4px)" }} data-testid="provider-edit-modal">
      <form onSubmit={submit} className="w-full max-w-lg border border-[color:var(--line)] p-6" style={{ background: "var(--card)" }}>
        <div className="flex justify-between items-center mb-4">
          <div className="font-display italic text-2xl text-[color:var(--gold)] uppercase">{provider}</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-[color:var(--muted)]" /></button>
        </div>
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="provider-enabled-toggle" />
          <span className="text-[11px] tracking-widest uppercase text-[color:var(--ink)]">Enabled</span>
        </label>
        {fields.map((f) => (
          <label key={f.key} className="block mb-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)] mb-1">{f.label}</div>
            <input
              type={f.secret ? "password" : "text"}
              placeholder={f.placeholder || (f.secret ? "•••••••" : "")}
              value={config[f.key] ?? ""}
              onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
              className="bet-input font-mono text-xs"
              data-testid={`field-${provider}-${f.key}`}
            />
          </label>
        ))}
        <div className="text-[10px] text-[color:var(--muted)] mb-3">
          Leave a field blank to keep its current value (if set).
        </div>
        {err && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{err}</div>}
        <button type="submit" disabled={busy} className="spin-btn w-full" data-testid="provider-save-btn">
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </div>
  );
}
